import { BookingState, BookingStateUrlParameter } from '@xeetr/types/enums'
import {
  BaseBookingModel,
  FoodBookingModel,
  FoodBookingSeriesEditResult,
  FoodBookingSeriesResult,
  PostFoodBookingModel,
  PostFoodBookingSeriesModel,
  PostServiceBookingModel,
  ServiceBookingModel,
} from '@xeetr/types/models'
import { formatInTimeZone } from 'date-fns-tz'

import ApiService from './common/API.service'

import { BookingStateOption } from '@/constants/bookingStates'

type SingleBookingModel = FoodBookingModel | ServiceBookingModel

export class BookingService extends ApiService {
  getBookings = async (
    from: Date,
    until: Date | null,
    state: BookingStateOption['value'],
    timezone: string
  ): Promise<BaseBookingModel[]> => {
    const params: Record<string, string> = {
      from: formatInTimeZone(from, timezone, 'yyyy-MM-dd HH:mm:ss'),
      until: until ? formatInTimeZone(until, timezone, 'yyyy-MM-dd HH:mm:ss') : '',
    }

    if (state !== 'active') {
      params.status = state.toUpperCase()
    }

    const { data } = await this.get<BaseBookingModel[]>(this.buildTenantUrl('bookings'), { params })

    return data.map((booking) => ({
      ...booking,
      from: new Date(booking.from),
      until: booking.until ? new Date(booking.until) : undefined,
    }))
  }

  changeBookingState = async (
    bookingId: string,
    newState: BookingStateUrlParameter,
    reason?: string,
    comment?: string,
    sendEmail?: boolean,
    scope?: 'occurrence' | 'future'
  ): Promise<SingleBookingModel | SingleBookingModel[]> => {
    const { data } = await this.put<
      { reason?: string; comment?: string; sendEmail?: boolean },
      SingleBookingModel | SingleBookingModel[]
    >(
      this.buildTenantUrl(`booking/${bookingId}/${newState}`),
      { reason, comment, sendEmail },
      { params: { scope } }
    )
    const parseDates = (booking: SingleBookingModel): SingleBookingModel => ({
      ...booking,
      from: new Date(booking.from),
      until: booking.until ? new Date(booking.until) : undefined,
    })
    return Array.isArray(data) ? data.map(parseDates) : parseDates(data)
  }

  newBooking = async (
    booking: PostFoodBookingModel | PostServiceBookingModel,
    config: { mail: boolean; state?: BookingState },
    requestId?: string
  ): Promise<BaseBookingModel> => {
    const { mail, state } = config
    const headers = requestId ? { 'X-Request-ID': requestId } : undefined
    const { data } = await this.post<
      PostFoodBookingModel | PostServiceBookingModel,
      BaseBookingModel
    >(this.buildTenantUrl('booking'), booking, {
      params: { mail, state },
      headers,
    })
    return data
  }

  newBookingSeries = async (
    booking: PostFoodBookingSeriesModel,
    mail: boolean,
    requestId?: string
  ): Promise<FoodBookingSeriesResult> => {
    const headers = requestId ? { 'X-Request-ID': requestId } : undefined
    const { data } = await this.post<PostFoodBookingSeriesModel, FoodBookingSeriesResult>(
      this.buildTenantUrl('booking/series'),
      booking,
      { params: { mail }, headers }
    )
    return data
  }

  putBooking = async (
    mail: boolean,
    booking: PostFoodBookingModel | PostServiceBookingModel,
    scope?: 'occurrence' | 'future',
    state?: BookingState
  ): Promise<SingleBookingModel | FoodBookingSeriesEditResult> => {
    const { data } = await this.put<
      PostFoodBookingModel | PostServiceBookingModel,
      SingleBookingModel | FoodBookingSeriesEditResult
    >(this.buildTenantUrl('booking'), booking, {
      params: { mail, scope, state },
    })
    return data
  }

  deleteBooking = async (bookingId: string, scope?: 'occurrence' | 'future'): Promise<void> => {
    await this.delete(this.buildTenantUrl(`booking/${bookingId}`), { params: { scope } })
  }

  getCustomerBookings = async (customerId: string): Promise<BaseBookingModel[]> => {
    const { data } = await this.get<BaseBookingModel[]>(
      this.buildCommonUrl(`customer/${customerId}/bookings`)
    )

    return data.map((booking) => ({
      ...booking,
      from: new Date(booking.from),
      until: booking.until ? new Date(booking.until) : undefined,
    }))
  }
}
